import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  MEAL_SLOTS,
  addDays,
  childMayUseNutrition,
  portionFactor,
  roundGrams,
} from '@kraftwerk/shared';
import type { MealSlot } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import type {
  LocalFood,
  LocalHouseholdMember,
  LocalRecipe,
  LocalRecipeCookNote,
  LocalRecipeIngredient,
} from '../../db/local.js';
import { apiFetch } from '../../api.js';
import { currentOwnerId } from '../../auth.js';
import {
  deleteRecipe,
  duplicateRecipe,
  listCookNotes,
  listFoods,
  listIngredients,
  logRecipe,
  planMeal,
  stepsOf,
  today,
  updateRecipe,
} from '../../db/nutrition.js';
import { addNeedsToList, currentHouseholdId, listMembers, recipeNeeds } from '../../db/shopping.js';
import { RecipeMacroLine } from './RecipeMacroLine.js';
import { CookMode } from './CookMode.js';
import { RecipeImage } from './RecipeImage.js';
import { imageForHousehold } from '../../db/recipeImages.js';
import { useTimeZone } from '../../hooks/useToday.js';
import { formatDay } from '../../format.js';
import { useSwipeBack } from '../../gestures/SwipeProvider.js';

/**
 * Ein Gericht auf einer Seite: alles, was man zum Nachschlagen, Planen und
 * Kochen braucht. Bearbeitet wird woanders — Lesen bleibt aufgeräumt.
 *
 * „Portionen" rechnet nur die **Anzeige** um (Mengen der Zutaten); das Rezept
 * bleibt, wie es ist. Was gegessen wurde, fragt „Heute gegessen" eigens ab —
 * gekocht für vier heißt nicht gegessen für vier.
 */
export function RecipeDetail({
  recipeId,
  onBack,
  onEdit,
  onOpen,
}: {
  recipeId: string;
  onBack: () => void;
  onEdit: () => void;
  /** Ein anderes Gericht bearbeiten — die eben angelegte Variante. */
  onOpen: (id: string) => void;
}): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const recipe = useLiveQuery(() => db.recipes.get(recipeId), [recipeId]);
  const ingredients = useLiveQuery(
    () => listIngredients(recipeId, db),
    [recipeId],
    [] as LocalRecipeIngredient[],
  );
  const foods = useLiveQuery(() => listFoods(db), [], [] as LocalFood[]);
  const notes = useLiveQuery(
    () => listCookNotes(recipeId, db),
    [recipeId],
    [] as LocalRecipeCookNote[],
  );
  const [wanted, setWanted] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [cooking, setCooking] = useState(false);

  if (recipe === undefined) return null;
  if (!recipe || recipe.deletedAt) {
    return (
      <div className="flex flex-col gap-3">
        <BackButton onBack={onBack} />
        <p className="text-sm text-ink-muted">{t('dish.gone')}</p>
      </div>
    );
  }

  const portions = wanted ?? recipe.portions;
  const factor = portionFactor(recipe.portions, portions);
  const byId = new Map(foods.map((food) => [food.id, food]));
  const steps = stepsOf(recipe);

  if (cooking) {
    return (
      <CookMode
        recipe={recipe}
        ingredients={ingredients}
        foods={byId}
        factor={factor}
        portions={portions}
        onClose={() => setCooking(false)}
      />
    );
  }

  return (
    <article aria-labelledby="dish-heading" className="flex flex-col gap-4">
      <BackButton onBack={onBack} />

      <RecipeImage
        recipe={recipe}
        className={`w-full rounded-2xl ${recipe.imageAt ? 'aspect-[16/9]' : 'aspect-[5/1]'}`}
      />

      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="dish-heading" className="font-display text-2xl font-bold break-words">
            {recipe.name}
          </h2>
          <p className="text-sm text-ink-muted">{t(`nutrition.slot.${recipe.slot}`)}</p>
        </div>
        <button
          type="button"
          aria-pressed={recipe.isFavorite}
          aria-label={t('dish.favorite')}
          onClick={() => void updateRecipe(recipe.id, { isFavorite: !recipe.isFavorite }, db)}
          className={`kw-btn kw-btn-quiet kw-btn-small shrink-0 px-3 text-lg ${
            recipe.isFavorite ? 'border-accent text-accent' : ''
          }`}
        >
          {recipe.isFavorite ? '★' : '☆'}
        </button>
      </header>

      <section className="kw-tile flex flex-col gap-1">
        <h3 className="kw-kicker">{t('dish.perPortion')}</h3>
        <p className="text-sm">
          <RecipeMacroLine recipe={recipe} />
        </p>
      </section>

      <section aria-labelledby="dish-ingredients" className="kw-tile flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 id="dish-ingredients" className="kw-kicker">
            {t('nutrition.recipe.ingredients')}
          </h3>
          <PortionStepper value={portions} onChange={setWanted} />
        </div>
        <IngredientLines recipe={recipe} ingredients={ingredients} foods={byId} factor={factor} />
      </section>

      {steps.length > 0 ? (
        <section aria-labelledby="dish-steps" className="kw-tile flex flex-col gap-2">
          <h3 id="dish-steps" className="kw-kicker">
            {t('dish.steps')}
          </h3>
          <ol className="flex flex-col gap-3">
            {steps.map((step, index) => (
              <li key={index} className="flex gap-3 text-sm">
                <span className="kw-figure w-6 shrink-0 text-right text-accent">{index + 1}</span>
                <span className="min-w-0 break-words">{step}</span>
              </li>
            ))}
          </ol>
        </section>
      ) : null}

      {recipe.prepNote || recipe.note || recipe.sourceNote || recipe.sourceUrl ? (
        <section className="kw-tile flex flex-col gap-1 text-sm">
          {recipe.prepNote ? (
            <p>{t('nutrition.recipe.prepNoteShown', { note: recipe.prepNote })}</p>
          ) : null}
          {recipe.note ? <p className="whitespace-pre-line">{recipe.note}</p> : null}
          {recipe.sourceNote ? (
            <p className="text-xs text-ink-muted">
              {t('nutrition.recipe.source', { note: recipe.sourceNote })}
            </p>
          ) : null}
          {recipe.sourceUrl ? (
            <a
              href={recipe.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs break-all text-accent underline"
            >
              {t('dish.sourceLink', { host: hostOf(recipe.sourceUrl) })}
            </a>
          ) : null}
        </section>
      ) : null}

      {steps.length > 0 || ingredients.length > 0 || recipe.items ? (
        <button type="button" onClick={() => setCooking(true)} className="kw-btn">
          {t('cook.open', { count: portions })}
        </button>
      ) : null}

      <Actions recipe={recipe} portions={portions} />

      {notes.length > 0 ? (
        <section aria-labelledby="dish-notes" className="kw-tile flex flex-col gap-2">
          <h3 id="dish-notes" className="kw-kicker">
            {t('dish.notes')}
          </h3>
          <ul className="flex flex-col gap-2">
            {notes.slice(0, 5).map((note) => (
              <li key={note.id} className="text-sm">
                <span className="text-xs text-ink-muted">
                  {formatDay(note.cookedOn, 'short', i18n.language)}
                  {note.rating === 1 ? ` · ${t('dish.ratingUp')}` : null}
                  {note.rating === -1 ? ` · ${t('dish.ratingDown')}` : null}
                </span>
                {note.note ? <p className="break-words">{note.note}</p> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="flex flex-col gap-2 border-t border-line pt-4">
        <button type="button" onClick={onEdit} className="kw-btn kw-btn-quiet kw-btn-small">
          {t('nutrition.library.edit')}
        </button>
        <button
          type="button"
          onClick={() => {
            void duplicateRecipe(recipe.id, t('dish.variantName', { name: recipe.name }), db).then(
              (id) => {
                if (id) onOpen(id);
              },
            );
          }}
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('dish.duplicate')}
        </button>
        <ShareWithHousehold recipeId={recipe.id} />
        {confirmDelete ? (
          <div role="alert" className="flex flex-col gap-2 rounded-xl border border-danger p-3">
            <p className="text-sm text-danger">{t('dish.deleteWarning')}</p>
            <button
              type="button"
              onClick={() => {
                void deleteRecipe(recipe.id, db).then(onBack);
              }}
              className="kw-btn kw-btn-small bg-danger"
            >
              {t('nutrition.library.delete')}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="kw-btn kw-btn-quiet kw-btn-small"
            >
              {t('nutrition.library.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="kw-btn kw-btn-quiet kw-btn-small text-danger"
          >
            {t('dish.delete')}
          </button>
        )}
      </div>
    </article>
  );
}

function BackButton({ onBack }: { onBack: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  // Wo ein Zurück-Knopf steht, wirkt auch das Wischen vom linken Rand.
  useSwipeBack(onBack);
  return (
    <button
      type="button"
      onClick={onBack}
      className="kw-btn kw-btn-quiet kw-btn-small self-start px-3"
    >
      ← {t('dish.back')}
    </button>
  );
}

function PortionStepper({
  value,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(0.5, value - (value > 1 ? 1 : 0.5)))}
        disabled={value <= 0.5}
        aria-label={t('dish.fewerPortions')}
        className="kw-step size-11"
      >
        −
      </button>
      <span role="status" className="min-w-20 text-center text-sm">
        {t('dish.portions', { count: value })}
      </span>
      <button
        type="button"
        onClick={() => onChange(value < 1 ? 1 : value + 1)}
        aria-label={t('dish.morePortions')}
        className="kw-step size-11"
      >
        +
      </button>
    </div>
  );
}

/**
 * Zutaten, umgerechnet auf die gewählten Portionen. Freie Zeilen stehen mit
 * ihrem Text da und sagen, dass sie nicht mitzählen; ein gelöschtes
 * Lebensmittel sagt das ebenso, statt zu verschwinden.
 */
function IngredientLines({
  recipe,
  ingredients,
  foods,
  factor,
}: {
  recipe: LocalRecipe;
  ingredients: LocalRecipeIngredient[];
  foods: Map<string, LocalFood>;
  factor: number;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (ingredients.length === 0) {
    return recipe.items ? (
      <p className="text-sm whitespace-pre-line">{recipe.items}</p>
    ) : (
      <p className="text-sm text-ink-muted">{t('dish.noIngredients')}</p>
    );
  }

  return (
    <ul className="flex flex-col divide-y divide-line">
      {ingredients.map((entry) => {
        if (entry.foodId === null) {
          return (
            <li key={entry.id} className="flex items-baseline justify-between gap-3 py-2 text-sm">
              <span className="min-w-0 break-words">{entry.label}</span>
              <span className="shrink-0 text-xs text-ink-muted">{t('dish.noNutrition')}</span>
            </li>
          );
        }
        const food = foods.get(entry.foodId);
        const grams = roundGrams((entry.grams ?? 0) * factor);
        const count =
          entry.portionCount !== null && entry.portionCount !== undefined && food?.portionName
            ? Math.round(entry.portionCount * factor * 10) / 10
            : null;
        return (
          <li key={entry.id} className="flex items-baseline justify-between gap-3 py-2 text-sm">
            <span className="min-w-0 break-words">
              {food?.name ?? t('nutrition.recipe.unknownFood')}
            </span>
            <span className="kw-figure shrink-0 text-right">
              {count !== null ? `${count} × ${food?.portionName} · ` : ''}
              {grams} g
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** Einplanen, eintragen, einkaufen — die drei Wege vom Rezept in den Alltag. */
function Actions({
  recipe,
  portions,
}: {
  recipe: LocalRecipe;
  portions: number;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const dayId = useId();
  const slotId = useId();
  const timezone = useTimeZone() ?? 'Europe/Berlin';
  const [open, setOpen] = useState<'plan' | 'eat' | null>(null);
  const [day, setDay] = useState('');
  const [slot, setSlot] = useState<MealSlot>(recipe.slot);
  const [eaten, setEaten] = useState(1);
  const [message, setMessage] = useState<string | null>(null);

  const todayIso = today(timezone);
  const days = Array.from({ length: 7 }, (_, index) => addDays(todayIso, index));

  const shop = async () => {
    const householdId = await currentHouseholdId(db);
    if (!householdId) {
      setMessage(t('dish.noHousehold'));
      return;
    }
    const needs = await recipeNeeds(recipe.id, portions, householdId, db);
    const lines = needs.lines.filter((line) => line.status === 'buy' || line.status === 'check');
    const added = lines.length > 0 ? await addNeedsToList(lines, db) : 0;
    setMessage(added > 0 ? t('dish.addedToList', { count: added }) : t('dish.nothingMissing'));
  };

  return (
    <section aria-label={t('dish.actions')} className="flex flex-col gap-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <button
          type="button"
          aria-expanded={open === 'eat'}
          onClick={() => setOpen(open === 'eat' ? null : 'eat')}
          className="kw-btn"
        >
          {t('dish.eatToday')}
        </button>
        <button
          type="button"
          aria-expanded={open === 'plan'}
          onClick={() => {
            setDay(days[1] ?? todayIso);
            setOpen(open === 'plan' ? null : 'plan');
          }}
          className="kw-btn kw-btn-quiet"
        >
          {t('dish.plan')}
        </button>
        <button type="button" onClick={() => void shop()} className="kw-btn kw-btn-quiet">
          {t('dish.shop')}
        </button>
      </div>

      {open === 'eat' ? (
        <form
          className="kw-tile flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void logRecipe({ recipe, date: todayIso, portions: eaten }, db).then((entry) => {
              setOpen(null);
              setMessage(entry ? t('dish.logged', { count: eaten }) : t('dish.cannotLog'));
            });
          }}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm">{t('dish.howMuch')}</span>
            <PortionStepper value={eaten} onChange={setEaten} />
          </div>
          <button type="submit" className="kw-btn kw-btn-small">
            {t('dish.logConfirm')}
          </button>
        </form>
      ) : null}

      {open === 'plan' ? (
        <form
          className="kw-tile flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            void planMeal({ date: day, slot, recipeId: recipe.id }, db).then(() => {
              setOpen(null);
              setMessage(t('dish.planned', { day: formatDay(day, 'short', i18n.language) }));
            });
          }}
        >
          <label htmlFor={dayId} className="flex flex-col gap-1">
            <span className="kw-label">{t('dish.day')}</span>
            <select
              id={dayId}
              value={day}
              onChange={(event) => setDay(event.target.value)}
              className="kw-field"
            >
              {days.map((option) => (
                <option key={option} value={option}>
                  {formatDay(option, 'short', i18n.language)}
                </option>
              ))}
            </select>
          </label>
          <label htmlFor={slotId} className="flex flex-col gap-1">
            <span className="kw-label">{t('dish.meal')}</span>
            <select
              id={slotId}
              value={slot}
              onChange={(event) => setSlot(event.target.value as MealSlot)}
              className="kw-field"
            >
              {MEAL_SLOTS.map((option) => (
                <option key={option} value={option}>
                  {t(`nutrition.slot.${option}`)}
                </option>
              ))}
            </select>
          </label>
          <button type="submit" className="kw-btn kw-btn-small">
            {t('dish.planConfirm')}
          </button>
        </form>
      ) : null}

      <p role="status" className="text-sm text-good">
        {message}
      </p>
    </section>
  );
}

/**
 * „An den Haushalt geben": legt beim anderen eine eigene Kopie an (Server,
 * `POST /recipes/:id/share`). Geht nur online — eine Kopie in einem fremden
 * Konto kann das Gerät nicht vorab anlegen.
 */
function ShareWithHousehold({ recipeId }: { recipeId: string }): React.JSX.Element | null {
  const { t } = useTranslation();
  const householdId = useLiveQuery(() => currentHouseholdId(db), [], null);
  const members = useLiveQuery(
    () => listMembers(householdId ?? null, db),
    [householdId],
    [] as LocalHouseholdMember[],
  );
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<{ text: string; ok: boolean } | null>(null);
  const me = currentOwnerId();
  // Kinder haben keine Ernährung — ein Gericht dort sähe niemand. Ausnahme:
  // ein Teen mit Freigabe der Eltern (27.10.2026).
  const others = members.filter(
    (member) =>
      member.userId !== me &&
      (member.role !== 'child' ||
        childMayUseNutrition({ level: member.childLevel, nutrition: member.teenNutrition })),
  );

  if (others.length === 0) return null;

  const give = (member: LocalHouseholdMember) => {
    void (async () => {
      // Verschlüsseltes Bild (E21) für den Haushalt neu packen.
      const recipe = await db.recipes.get(recipeId);
      const image = recipe ? await imageForHousehold(recipe, db) : undefined;
      return apiFetch<{ copiedFoods: number }>(`/recipes/${recipeId}/share`, {
        method: 'POST',
        body: JSON.stringify({ toUserId: member.userId, ...(image ? { image } : {}) }),
      });
    })()
      .then(() => setMessage({ text: t('dish.shared', { name: member.displayName }), ok: true }))
      .catch(() => setMessage({ text: t('dish.shareFailed'), ok: false }))
      .finally(() => setOpen(false));
  };

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="kw-btn kw-btn-quiet kw-btn-small"
      >
        {t('dish.share')}
      </button>
      {open ? (
        <div className="kw-tile flex flex-col gap-2">
          <p className="text-xs text-ink-muted">{t('dish.shareHint')}</p>
          {others.map((member) => (
            <button
              key={member.userId}
              type="button"
              onClick={() => give(member)}
              className="kw-btn kw-btn-small"
            >
              {t('dish.shareTo', { name: member.displayName })}
            </button>
          ))}
        </div>
      ) : null}
      {message ? (
        <p role="status" className={`text-sm ${message.ok ? 'text-good' : 'text-danger'}`}>
          {message.text}
        </p>
      ) : null}
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
